import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { register } from "../../actions/authActions";
import Navbar from "../../components/Navbar/Navbar";
import {
  Container,
  Form,
  Button,
  Spinner,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import Footer from "../../components/Footer/Footer";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptySignupErrors = {
  username: "",
  email: "",
  phone: "",
  address: "",
  password: "",
  confirm_password: "",
  role_id: "",
};

const DEFAULT_SIGNUP_ERROR =
  "Unable to sign up. Please check the highlighted fields and try again.";

const isHtmlError = (value) => {
  const text = String(value || "").trim().toLowerCase();

  return (
    text.startsWith("<!doctype html") ||
    text.startsWith("<html") ||
    text.includes("<body") ||
    text.includes("<title>403 forbidden</title>") ||
    text.includes("csrf verification failed") ||
    text.includes("origin checking failed")
  );
};

const cleanSignupError = (value) => {
  const message = String(value || "").trim();

  if (!message) {
    return "";
  }

  if (isHtmlError(message)) {
    return "Signup request was blocked by the server. Please refresh the page and try again.";
  }

  return message;
};

const normalizeSignupFieldErrors = (fieldErrors = {}) => ({
  ...emptySignupErrors,
  username: fieldErrors.username || "",
  email: fieldErrors.email || "",
  phone: fieldErrors.phone || "",
  address: fieldErrors.address || "",
  password: fieldErrors.password || "",
  role_id:
    fieldErrors.role_id ||
    fieldErrors.role_name ||
    fieldErrors.role ||
    "",
});

const Signup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [apiSuccess, setApiSuccess] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    confirm_password: "",
    role_id: "b2crole",
  });

  const [errors, setErrors] = useState(emptySignupErrors);

  const {
    username,
    email,
    phone,
    address,
    password,
    confirm_password,
    role_id,
  } = formData;

  const displayError = useMemo(() => {
    return cleanSignupError(apiError);
  }, [apiError]);

  useEffect(() => {
    if (displayError) {
      setShowErrorToast(true);
    }
  }, [displayError]);

  useEffect(() => {
    if (apiSuccess) {
      setShowSuccessToast(true);
    }
  }, [apiSuccess]);

  const closeErrorToast = () => {
    setShowErrorToast(false);
    setApiError("");
  };

  const closeSuccessToast = () => {
    setShowSuccessToast(false);
    setApiSuccess("");
  };

  const onChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
    setApiError("");
    setApiSuccess("");
    setShowErrorToast(false);
    setShowSuccessToast(false);
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next = { ...emptySignupErrors };

    if (!username.trim()) {
      next.username = "Username is required.";
    }

    if (!email.trim()) {
      next.email = "Email is required.";
    } else if (!emailRegex.test(email.trim())) {
      next.email = "Enter a valid email.";
    }

    if (!address.trim()) {
      next.address = "Address is required.";
    }

    if (!String(phone).trim()) {
      next.phone = "Phone is required.";
    }

    if (!role_id) {
      next.role_id = "Role is required.";
    }

    if (!password) {
      next.password = "Password is required.";
    } else if (password.length < 6) {
      next.password = "Min 6 characters.";
    }

    if (!confirm_password) {
      next.confirm_password = "Confirm your password.";
    } else if (confirm_password !== password) {
      next.confirm_password = "Passwords do not match.";
    }

    setErrors(next);

    return Object.values(next).every((value) => !value);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!validate() || submitting) {
      return;
    }

    setSubmitting(true);
    setApiError("");
    setApiSuccess("");
    setShowErrorToast(false);
    setShowSuccessToast(false);

    try {
      const payload = {
        username: username.trim(),
        email: email.trim(),
        phone: String(phone).trim(),
        address: address.trim(),
        password,
        role_name: role_id,
      };

      const result = await dispatch(register(payload));

      if (result?.ok) {
        setApiSuccess("Account created successfully. Redirecting to login...");

        setTimeout(() => {
          navigate("/login", {
            replace: true,
            state: { signupSuccess: true },
          });
        }, 1200);

        return;
      }

      setErrors(normalizeSignupFieldErrors(result?.fieldErrors));
      setApiError(result?.error || DEFAULT_SIGNUP_ERROR);
      setShowErrorToast(true);
    } catch (err) {
      setApiError("Unable to sign up. Please try again.");
      setShowErrorToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Navbar />

      <ToastContainer
        position="top-end"
        className="p-3"
        style={{
          position: "fixed",
          top: "1rem",
          right: "1rem",
          zIndex: 9999,
        }}
      >
        <Toast
          bg="danger"
          show={Boolean(displayError) && showErrorToast}
          onClose={closeErrorToast}
          delay={6000}
          autohide
        >
          <Toast.Header closeButton>
            <strong className="me-auto">Signup error</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{displayError}</Toast.Body>
        </Toast>

        <Toast
          bg="success"
          show={Boolean(apiSuccess) && showSuccessToast}
          onClose={closeSuccessToast}
          delay={4500}
          autohide
        >
          <Toast.Header closeButton>
            <strong className="me-auto">Signup successful</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{apiSuccess}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Container fluid className="d-flex justify-content-center">
        <Form className="form-container p-4" onSubmit={onSubmit} noValidate>
          <h3>Signup</h3>

          <Form.Group className="form-column">
            <Form.Label>Username</Form.Label>
            <Form.Control
              type="text"
              name="username"
              value={username}
              placeholder="Username"
              onChange={onChange}
              isInvalid={Boolean(errors.username)}
              disabled={submitting}
              autoComplete="username"
              required
            />
            <Form.Control.Feedback type="invalid">
              {errors.username}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={email}
              placeholder="Email"
              onChange={onChange}
              isInvalid={Boolean(errors.email)}
              disabled={submitting}
              autoComplete="email"
              required
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Address</Form.Label>
            <Form.Control
              type="text"
              name="address"
              value={address}
              placeholder="Address"
              onChange={onChange}
              isInvalid={Boolean(errors.address)}
              disabled={submitting}
              autoComplete="street-address"
              required
            />
            <Form.Control.Feedback type="invalid">
              {errors.address}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="tel"
              name="phone"
              value={phone}
              placeholder="Phone"
              onChange={onChange}
              isInvalid={Boolean(errors.phone)}
              disabled={submitting}
              autoComplete="tel"
              required
            />
            <Form.Control.Feedback type="invalid">
              {errors.phone}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              name="password"
              value={password}
              placeholder="Password"
              onChange={onChange}
              isInvalid={Boolean(errors.password)}
              disabled={submitting}
              autoComplete="new-password"
              required
            />
            <Form.Control.Feedback type="invalid">
              {errors.password}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Confirm Password</Form.Label>
            <Form.Control
              type="password"
              name="confirm_password"
              value={confirm_password}
              placeholder="Confirm Password"
              onChange={onChange}
              isInvalid={Boolean(errors.confirm_password)}
              disabled={submitting}
              autoComplete="new-password"
              required
            />
            <Form.Control.Feedback type="invalid">
              {errors.confirm_password}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Role</Form.Label>
            <Form.Control
              as="select"
              name="role_id"
              value={role_id}
              onChange={onChange}
              isInvalid={Boolean(errors.role_id)}
              disabled={submitting}
              required
            >
              <option value="" disabled>
                Select role
              </option>
              <option value="fleetrole">Distributors/Retailers</option>
              <option value="companyrole">Fleets/Corporate Clients</option>
              <option value="thirdprole">3rd Party Suppliers</option>
              <option value="workshoprole">Workshops/Garages</option>
              <option value="b2crole">B2C Customers</option>
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.role_id}
            </Form.Control.Feedback>
          </Form.Group>

          <Button
            variant="primary"
            type="submit"
            className="login-button d-inline-flex align-items-center gap-2"
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting && <Spinner animation="border" size="sm" />}
            {submitting ? "Submitting..." : "Signup"}
          </Button>

          <div className="mt-3">
            Do you have an account?
            <Link to="/login">
              <span> Login here</span>
            </Link>
          </div>
        </Form>
      </Container>

      <Footer />
    </div>
  );
};

export default Signup;